import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Divider,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDebounce } from '@revelio/core';
import type { Item } from '@revelio/iso-utility';
import { Text } from '@revelio/ui';

import { fetchCompanySearchResults } from '../../api/fetchCompanySearchResults';
import type { CompanyResultItem } from '../../types/company-result-item';
import { TreeSelection } from '../tree/TreeSelection';
import type { TreeData } from '../tree/useTreeApi';
import styles from './CompanySearchTree.module.css';
import { SelectedItems } from './SelectedItems';

export type CompanySearchTreeHandle = {
  handleClearSelections: () => void;
};

export interface CompanySearchTreeProps {
  placeholderListNodes: TreeData[];
  width?: React.CSSProperties['width'];
  maxHeight?: number;
  treeHeight?: number;
  selections?: Record<string, CompanyResultItem>;
  setSelections: React.Dispatch<
    React.SetStateAction<Record<string, CompanyResultItem>>
  >;
  sortFn?: (a: Item, b: Item) => number;
  inputGroupSize?: string;
  autoFocus?: boolean;
  singleSelect?: boolean;
  transformResults?: (results: Item[]) => Item[];
  noFilteredResultsMessage?: string;
}

const MIN_SEARCH_LENGTH = 3;
const CACHE_TTL_MS = 1000 * 60 * 5;

const populateCompanyMap = (
  nodes: TreeData[],
  map: Map<string, CompanyResultItem>
) => {
  for (const node of nodes) {
    if (node.children?.length) {
      populateCompanyMap(node.children, map);
    } else {
      const rcidMatch = /^(?:.*[._:])?(\d+)$/.exec(node.id);
      if (!rcidMatch) continue;

      map.set(node.id, {
        rcid: Number(rcidMatch[1]),
        primary_name: node.name,
        company_id: node.id,
        raw_emp_count: node.nodeCount ?? 0,
        has_subsidiaries: false,
      });
    }
  }
};

const toCompanyResultItem = (item: Item): CompanyResultItem => {
  const rcidValue = item.rcid ?? item.id;
  const companyIdValue = item.company_id ?? item.id ?? rcidValue;
  const primaryIdentifiers =
    typeof item.primary_identifiers === 'object' &&
    item.primary_identifiers !== null
      ? (item.primary_identifiers as CompanyResultItem['primary_identifiers'])
      : undefined;

  return {
    primary_name: String(item.primary_name ?? rcidValue ?? ''),
    company_id: String(companyIdValue ?? ''),
    rcid: Number(rcidValue ?? 0),
    raw_emp_count:
      typeof item.raw_emp_count === 'number'
        ? item.raw_emp_count
        : Number(item.raw_emp_count ?? 0),
    has_subsidiaries:
      typeof item.has_subsidiaries === 'boolean'
        ? item.has_subsidiaries
        : false,
    ...(primaryIdentifiers ? { primary_identifiers: primaryIdentifiers } : {}),
  };
};

const addDisabledToParents = (nodes: TreeData[]): TreeData[] =>
  nodes.map((node) => ({
    ...node,
    disabled: !!node.children?.length,
    children: node.children?.length
      ? addDisabledToParents(node.children)
      : undefined,
  }));

export const CompanySearchTree = forwardRef<
  CompanySearchTreeHandle,
  CompanySearchTreeProps
>(
  (
    {
      placeholderListNodes,
      width = '100%',
      maxHeight,
      selections = {},
      setSelections,
      treeHeight = 280,
      sortFn,
      inputGroupSize = 'md',
      autoFocus = false,
      singleSelect = false,
      transformResults,
      noFilteredResultsMessage,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>('');
    const debouncedSearch = useDebounce(inputValue, 200);
    const companyDataByIdRef = useRef<Map<string, CompanyResultItem>>(
      new Map()
    );
    const hasRekeyed = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (autoFocus) {
        inputRef.current?.focus();
      }
    }, [autoFocus]);

    useImperativeHandle(ref, () => ({
      handleClearSelections: () => {
        setSelections({});
        setInputValue('');
      },
    }));

    const isInputActive = inputValue.trim().length >= MIN_SEARCH_LENGTH;
    const isSearchActive =
      isInputActive && debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

    const toast = useToast();

    const {
      data: queryData,
      isFetching,
      isError,
    } = useQuery({
      queryKey: ['company-search', debouncedSearch],
      queryFn: async ({ signal }) => {
        const results = await fetchCompanySearchResults(
          debouncedSearch,
          signal
        );
        return results as unknown as Item[];
      },
      enabled: isSearchActive,
      staleTime: CACHE_TTL_MS,
      gcTime: CACHE_TTL_MS,
    });

    useEffect(() => {
      if (isError) {
        toast({
          title: 'Search error',
          description:
            'Something went wrong searching companies. Please try again later.',
          status: 'error',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }, [isError, toast]);

    useEffect(() => {
      if (placeholderListNodes.length === 0) return;

      if (!hasRekeyed.current) {
        populateCompanyMap(placeholderListNodes, companyDataByIdRef.current);
        setSelections((prev) => {
          if (Object.keys(prev).length === 0) return prev;
          const rcidToPathId = new Map<number, string>();
          for (const [pathId, co] of companyDataByIdRef.current) {
            rcidToPathId.set(co.rcid, pathId);
          }
          const updated: Record<string, CompanyResultItem> = {};
          for (const company of Object.values(prev)) {
            const pathId = rcidToPathId.get(company.rcid) ?? company.company_id;
            updated[pathId] = { ...company, company_id: pathId };
          }
          hasRekeyed.current = true;
          return updated;
        });
      }
    }, [placeholderListNodes, setSelections]);

    useEffect(() => {
      if (!queryData) return;
      const results = transformResults
        ? transformResults(queryData)
        : queryData;
      results.forEach((item) => {
        const companyResultItem = toCompanyResultItem(item);
        companyDataByIdRef.current.set(
          String(companyResultItem.rcid),
          companyResultItem
        );
      });
    }, [queryData, transformResults]);

    const handleSelectionChange = useCallback(
      (newIds: string[]) => {
        setSelections((prev) => {
          const updated: Record<string, CompanyResultItem> = {};
          for (const id of newIds) {
            updated[id] = prev[id] ??
              companyDataByIdRef.current.get(id) ?? {
                rcid: 0,
                primary_name: id,
                company_id: id,
                raw_emp_count: 0,
                has_subsidiaries: false,
              };
          }
          return updated;
        });
      },
      [setSelections]
    );

    const treeData = useMemo(() => {
      if (isSearchActive) {
        const results = transformResults
          ? transformResults(queryData ?? [])
          : (queryData ?? []);
        const sorted = sortFn ? [...results].sort(sortFn) : results;
        return sorted.map(
          (item): TreeData => ({
            id: String(item.rcid ?? item.id),
            name: String(item.primary_name ?? item.rcid),
            depth: 0,
            nodeCount: Number(item.raw_emp_count) || 0,
          })
        );
      }
      return addDisabledToParents(placeholderListNodes);
    }, [
      isSearchActive,
      queryData,
      placeholderListNodes,
      sortFn,
      transformResults,
    ]);

    const selectedIds = useMemo(() => Object.keys(selections), [selections]);

    const handleRemove = useCallback(
      (companyId: string) => {
        setSelections((prev) => {
          const updated = { ...prev };
          delete updated[companyId];
          return updated;
        });
      },
      [setSelections]
    );

    return (
      <div className={styles.container} style={{ width, maxHeight }}>
        <div className={styles.inner}>
          <div className={styles.searchWrapper}>
            <InputGroup size={inputGroupSize}>
              <Input
                ref={inputRef}
                placeholder="Search..."
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value.toLowerCase());
                }}
                backgroundColor="#ffffff"
                data-testid="async-tree-search-input"
              />
              <InputRightElement>
                {(() => {
                  if (isFetching) {
                    return (
                      <Spinner
                        w={3.5}
                        h={3.5}
                        color="green.600"
                        data-testid="async-tree-search-input-loading"
                      />
                    );
                  }
                  if (inputValue === '') {
                    return <SearchIcon w={3.5} h={3.5} color="silver.600" />;
                  }
                  return (
                    <CloseIcon
                      w={2.5}
                      h={2.5}
                      color="silver.600"
                      onClick={() => setInputValue('')}
                      cursor="pointer"
                    />
                  );
                })()}
              </InputRightElement>
            </InputGroup>
          </div>
          {Object.values(selections).length > 0 && (
            <>
              <SelectedItems
                items={Object.values(selections).map((c) => ({
                  id: c.company_id,
                  name: c.primary_name,
                }))}
                onRemove={handleRemove}
              />
              <Divider mb={2} />
            </>
          )}
          <div onWheel={(e) => e.stopPropagation()}>
            {(() => {
              const trimmedLen = inputValue.trim().length;
              if (trimmedLen > 0 && trimmedLen < MIN_SEARCH_LENGTH) {
                return (
                  <Text fontSize="x-sm" className={styles.searchStatus}>
                    Type at least {MIN_SEARCH_LENGTH} characters to search
                  </Text>
                );
              }
              if (isSearchActive && !isFetching) {
                if ((queryData ?? []).length === 0) {
                  return (
                    <Text fontSize="x-sm" className={styles.searchStatus}>
                      No results found
                    </Text>
                  );
                }
                if (noFilteredResultsMessage && treeData.length === 0) {
                  return (
                    <Text fontSize="x-sm" className={styles.searchStatus}>
                      {noFilteredResultsMessage}
                    </Text>
                  );
                }
              }
              return null;
            })()}
            <TreeSelection
              key={isSearchActive ? 'search' : 'placeholder'}
              data={treeData}
              value={selectedIds}
              onChange={handleSelectionChange}
              height={treeHeight}
              skipDefaultSort
              singleSelect={singleSelect}
              disabled={isSearchActive ? false : undefined}
              isLoading={isFetching}
            />
          </div>
        </div>
      </div>
    );
  }
);
